import React from 'react';
import { FiLink, FiUsers, FiBarChart2, FiTrendingUp, FiDollarSign } from 'react-icons/fi';
import StatCard from './StatCard';
import EmptyState from './EmptyState';
import { formatCurrency } from '../utils';

export default function OverviewTab({ overview, topAffiliates, loading }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Links"
          value={overview?.totalLinks ?? 0}
          icon={<FiLink className="h-6 w-6" />}
          colorKey="orange"
        />
        <StatCard
          title="Active Links"
          value={overview?.activeLinks ?? 0}
          subtitle={
            overview?.totalLinks
              ? `${Math.round((overview.activeLinks / overview.totalLinks) * 100)}% active`
              : ''
          }
          icon={<FiUsers className="h-6 w-6" />}
          colorKey="blue"
        />
        <StatCard
          title="Total Clicks"
          value={(overview?.totalClicks ?? 0).toLocaleString()}
          icon={<FiBarChart2 className="h-6 w-6" />}
          colorKey="violet"
        />
        <StatCard
          title="Conversions"
          value={overview?.totalConversions ?? 0}
          subtitle={
            overview?.conversionRate != null
              ? `${Number(overview.conversionRate).toFixed(2)}% rate`
              : ''
          }
          icon={<FiTrendingUp className="h-6 w-6" />}
          colorKey="emerald"
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(overview?.totalRevenue)}
          icon={<FiDollarSign className="h-6 w-6" />}
          colorKey="emerald"
        />
      </div>
      {topAffiliates.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <h3 className="text-base font-semibold text-slate-900">Top performers (preview)</h3>
            <p className="mt-0.5 text-sm text-slate-500">By conversions and revenue</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Affiliate
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                    Links
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                    Clicks
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                    Conversions
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {topAffiliates.slice(0, 5).map((a, i) => (
                  <tr key={a.userId?.toString() || i} className="hover:bg-slate-50/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm font-semibold text-orange-600">
                          {(a.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {a.name || 'Unknown'}
                          </p>
                          {a.email && (
                            <p className="max-w-[180px] truncate text-xs text-slate-500">
                              {a.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-slate-600">
                      {a.linksCount ?? 0}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-slate-600">
                      {(a.clicks ?? 0).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right text-sm text-slate-600">
                      {a.conversions ?? 0}
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-slate-900">
                      {formatCurrency(a.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!loading && overview && topAffiliates.length === 0 && (
        <EmptyState
          message="No affiliate data yet"
          subMessage="Links, clicks, and conversions will appear here."
        />
      )}
    </div>
  );
}
